import { useCallback, useEffect, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./SealedPaywall.module.scss"

/**
 * SealedPaywall (`/sealed`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: FEATURE-SPECIFIC.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — this screen sells ONE
 * real security/privacy feature and nothing else: the encrypted CLOUD VAULT.
 *
 * ── The one feature ────────────────────────────────────────────────────────
 * Cloud Vault — a real Premium capability. The app keeps a second, ENCRYPTED
 * copy of every memory in your vault ("secure backups that stay encrypted").
 * The screen is opened FROM this one locked feature, so everything on it is
 * about that single capability: a sealed copy, kept off your phone, that opens
 * with your key only.
 *
 * ── Concept: "Sealed, and only yours." ─────────────────────────────────────
 * Not another lock on the front door, not a scramble, not a loss threat. The
 * feature is told as a small CEREMONY: each memory folds into a wax-sealed
 * keepsake and rises off your phone into the Cloud Vault above — a second copy
 * you never have to think about. The privacy half is proven honestly and
 * interactively: the sealed copies stay sealed by default; PRESS & HOLD YOUR
 * KEY and they bloom open — release and they re-seal. It opens for you, and it
 * stays sealed for anyone else. Register: reassuring, warm-craft — the calm of
 * a second sealed copy, not fear.
 *
 * ── Divergence (vs. the 85+ prior concepts; see claude-paywalls/_ledger.md) ─
 * The security cluster is saturated — locks/keypads (Only You, Twofold), pattern
 * (Glyph/Trace/Keyline), disguise/decoy (Plain Sight/Bystander/Decoy), break-in
 * (Caught/Sentry), face-down (Reflex/Flipside). Cloud Vault is the least-mined
 * real security feature. It differs deliberately from the two backup-adjacent
 * priors it is closest to: NOT Cipher's encrypted-static-→-memories scramble
 * (here the metaphor is a wax SEAL / sealed letter, possession not distortion),
 * and NOT Backup's loss-aversion ("gone forever"). Value frame = privacy of a
 * sealed copy; hero motif = memories folding into wax-sealed envelopes that rise
 * to a luminous cloud tier; interaction = a press-&-hold KEY that blooms the
 * sealed copies open; register = ceremonial-reassurance. App-Store-compliant:
 * additive framing, NO data-loss-on-expiration claim, real "encrypted" language,
 * exact Save % from live prices, no countdown, no fake scarcity.
 *
 * Self-contained: renders on debug data (?debug=true) and bundled /byheart
 * photos; no native injection required.
 */

// Six memories, kept on the phone; a sealed COPY of each rises to the cloud.
// Bundled in /public/byheart (reused repo asset set) — nothing external.
const MEMS = [
  "/byheart/m05.jpg",
  "/byheart/m12.jpg",
  "/byheart/m13.jpg",
  "/byheart/m08.jpg",
  "/byheart/m17.jpg",
  "/byheart/m03.jpg",
]
const N = MEMS.length

// ── inline icons (stroke = currentColor) ──────────────────────────────────
const Crest = ({ s = 15 }: { s?: number }) => (
  <svg viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
    <path
      d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M9.2 11.7l2 2 3.6-3.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const KeyIcon = ({ s = 18 }: { s?: number }) => (
  <svg viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
    <circle
      cx="8.5"
      cy="8.5"
      r="4.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="8.5" cy="8.5" r="1.1" fill="currentColor" />
    <path
      d="M11.7 11.7L20 20M17 17l2-2M14.4 14.4l1.7-1.7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CloudIcon = ({ s = 18 }: { s?: number }) => (
  <svg viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
    <path
      d="M7 18h10.5a3.5 3.5 0 0 0 .4-6.98 5 5 0 0 0-9.6-1.2A3.9 3.9 0 0 0 7 18z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
)

const Check = ({ s = 13 }: { s?: number }) => (
  <svg viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

type Factor = {
  key: string
  name: string
  line: string
  Icon: () => JSX.Element
}

const FACTORS: Factor[] = [
  {
    key: "copy",
    name: "A second copy, off your phone",
    line: "Every memory is kept safe in the Cloud Vault — not only on a device you carry.",
    Icon: () => <CloudIcon s={18} />,
  },
  {
    key: "enc",
    name: "Encrypted, coming and going",
    line: "Secure backups that stay encrypted — sealed on the way up and while they rest.",
    Icon: () => (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <rect
          x="5"
          y="10.4"
          width="14"
          height="9.6"
          rx="2.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M8 10.4V8a4 4 0 0 1 8 0v2.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <circle cx="12" cy="15" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    key: "key",
    name: "Opens with your key only",
    line: "Hand someone the file and all they hold is a sealed copy. It blooms open for you.",
    Icon: () => <KeyIcon s={18} />,
  },
]

function SealedPaywall() {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = (() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const pct = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return pct > 0 ? pct : 79
  })()

  // Warm the safe-area insets to the deep stage so the screen reads as one
  // continuous surface from status bar to home indicator.
  useEffect(() => {
    const dark = state.initialData?.darkMode
    const inset = dark ? "#10151f" : "#eef2f9"
    actions.setHeaderInsetColor(inset)
    actions.setFooterInsetColor(inset)
  }, [actions, state.initialData?.darkMode])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [sealed, setSealed] = useState(0) // how many copies have risen to the cloud
  const [keyOpen, setKeyOpen] = useState(false) // holding your key blooms them open

  const timers = useRef<number[]>([])
  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }
  const after = (ms: number, fn: () => void) =>
    timers.current.push(window.setTimeout(fn, ms))

  // The sealing ceremony: one memory folds & rises every beat.
  const runSeal = useCallback(() => {
    clearTimers()
    setSealed(0)
    if (reduceMotion) {
      setSealed(N)
      return
    }
    for (let i = 1; i <= N; i++) {
      after(420 + i * 360, () => setSealed(i))
    }
  }, [reduceMotion])

  useEffect(() => {
    runSeal()
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const holdKey = useCallback(() => setKeyOpen(true), [])
  const releaseKey = useCallback(() => setKeyOpen(false), [])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const done = sealed >= N
  const status = keyOpen
    ? "Your key — and only your key — opens them."
    : done
      ? `${N} memories — a sealed copy of each, kept in the Cloud Vault.`
      : "Sealing a copy of each memory into the Cloud Vault…"

  const rootCls = [
    styles.root,
    keyOpen ? styles.opened : "",
    done ? styles.done : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={rootCls}>
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark}>
              <Crest s={14} />
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <CloudIcon s={13} />
            Cloud Vault · Encrypted backup
          </span>
          <h1 className={styles.title}>
            Sealed, and <em>only yours.</em>
          </h1>
          <p className={styles.sub}>
            Private Photo Vault keeps a second, encrypted copy of every memory —
            off your phone, and openable with your key alone.
          </p>
        </section>

        {/* ── the stage: phone below, Cloud Vault above ──────────────────── */}
        <section className={styles.stage} aria-hidden="true">
          {/* cloud tier — sealed copies gather here */}
          <div className={styles.cloud}>
            <span className={styles.cloudLabel}>
              <CloudIcon s={13} /> Cloud Vault
              <span className={styles.encTag}>
                {keyOpen ? "unsealed for you" : "encrypted"}
              </span>
            </span>
            <div className={styles.cloudGrid}>
              {MEMS.map((src, i) => {
                const filled = i < sealed
                return (
                  <span
                    key={i}
                    className={`${styles.env} ${filled ? styles.envIn : ""} ${
                      keyOpen ? styles.envOpen : ""
                    }`}
                    style={{ ["--i" as any]: i }}
                  >
                    <span
                      className={styles.envPhoto}
                      style={{ backgroundImage: `url(${src})` }}
                    />
                    <span className={styles.envSeal}>
                      <span className={styles.wax}>
                        <Crest s={13} />
                      </span>
                    </span>
                    <span className={styles.envKey}>
                      <KeyIcon s={14} />
                    </span>
                  </span>
                )
              })}
            </div>
          </div>

          {/* the sealing channel */}
          <span className={styles.beam}>
            <span className={styles.beamGlow} />
          </span>

          {/* phone tier — your memories, which stay right where they are */}
          <div className={styles.phone}>
            <span className={styles.phoneLabel}>On your phone</span>
            <div className={styles.phoneGrid}>
              {MEMS.map((src, i) => {
                const copied = i < sealed
                return (
                  <span key={i} className={styles.shot}>
                    <span
                      className={styles.shotPhoto}
                      style={{ backgroundImage: `url(${src})` }}
                    />
                    <span
                      className={`${styles.copied} ${copied ? styles.copiedOn : ""}`}
                    >
                      <Check s={11} />
                    </span>
                  </span>
                )
              })}
            </div>
          </div>
        </section>

        {/* meter + status */}
        <div className={styles.meterRow}>
          <span className={styles.meter}>
            <span
              className={styles.meterFill}
              style={{ width: `${(sealed / N) * 100}%` }}
            />
          </span>
          <span className={styles.meterNum}>
            {sealed}/{N} sealed
          </span>
        </div>
        <p className={styles.status} aria-live="polite">
          <span className={styles.statusDot} />
          {status}
        </p>

        {/* ── the proof: hold your key ───────────────────────────────────── */}
        <button
          className={`${styles.keyBtn} ${keyOpen ? styles.keyHeld : ""}`}
          onPointerDown={holdKey}
          onPointerUp={releaseKey}
          onPointerLeave={releaseKey}
          onPointerCancel={releaseKey}
          aria-pressed={keyOpen}
        >
          <span className={styles.keyGlyph}>
            <KeyIcon s={18} />
          </span>
          <span className={styles.keyText}>
            {keyOpen ? "Your key is turning…" : "Press & hold your key"}
            <span className={styles.keyHint}>
              {keyOpen
                ? "the sealed copies bloom open — for you"
                : "see what only you can open — sealed for anyone else"}
            </span>
          </span>
        </button>

        <button className={styles.replay} onClick={runSeal}>
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M4.5 12a7.5 7.5 0 1 1 2.2 5.3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
            />
            <path
              d="M4.2 17.6l.4-3.6 3.6.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Replay the seal
        </button>

        {/* ── the one feature, in detail ─────────────────────────────────── */}
        <ul className={styles.factors}>
          <li className={styles.factorsHead}>What the sealed copy gives you</li>
          {FACTORS.map((f, i) => (
            <li
              key={f.key}
              className={styles.factor}
              style={{ ["--d" as any]: i }}
            >
              <span className={styles.factorIcon}>
                <f.Icon />
              </span>
              <span className={styles.factorText}>
                <span className={styles.factorName}>{f.name}</span>
                <span className={styles.factorLine}>{f.line}</span>
              </span>
            </li>
          ))}
        </ul>

        <p className={styles.assure}>
          <span className={styles.assureMark}>
            <Crest s={13} />
          </span>
          The copy adds to what's already on your phone — nothing is moved, and
          nothing is ever deleted. Your originals stay exactly where they are.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Sealed for a year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check}>
                <Check s={13} />
              </span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or month-to-month</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Seal every memory — start ${trialDays}-day free trial`
              : "Seal every memory"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.fdot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.fdot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default SealedPaywall
